import {SagaIterator} from "redux-saga";
import {all, call, takeLatest} from "redux-saga/effects";
import {
	autoFillTeam,
	clearWithdrawn,
	createLeague,
	deleteRollbackLeagueRequest,
	fetchFaq,
	fetchFullTeam,
	fetchGameRules,
	fetchGlobalSwingsRankings,
	fetchLandingSettings,
	fetchLandingTiles,
	fetchLeagueMonthlyRankings,
	fetchLeagueRankings,
	fetchLeagueUsersList,
	fetchMoreGlobalSwingsRankings,
	fetchMoreLeagueMonthlyRankings,
	fetchMoreLeagueRankings,
	fetchMoreLeagueUsersList,
	fetchMoreRolexRankings,
	fetchMoreTilesLeaderboard,
	fetchMoreTopTenRankings,
	fetchPrivacy,
	fetchPrizes,
	fetchRankingsMonthsRequest,
	fetchRolexRankings,
	fetchSchedule,
	fetchSwingsJsonRequest,
	fetchTerms,
	fetchTilesLeaderboard,
	fetchTopTenRankings,
	globalError,
	inviteToLeague,
	joinTileRequest,
	joinToLeague,
	leaveLeague,
	loadMoreLeaguesForJoin,
	postContact,
	removeBooster,
	removeUserFromLeague,
	requestAllPlayersJSON,
	requestCountriesJSON,
	requestCurrentUser,
	requestEventsJSON,
	requestMyTeam,
	requestPlayersJSON,
	requestPlayerStatsJSON,
	requestResetPassword,
	requestRivalTeam,
	requestSeasonStatsJSON,
	resetPassword,
	rollbackAllLeaguesRequest,
	rollbackLeagueRequest,
	saveBooster,
	saveTeam,
	saveTeamFromLocalStorage,
	searchLeaguesForJoin,
	showLeague,
	showLeaguesForJoin,
	showMyLeagues,
	subscribeToLiveScores,
	updateLeague,
	userAcceptNextSeasonTerms,
	userAcceptTerms,
	userAcceptViewWelcomeModal,
	userInfoUpdate,
	userLogin,
	userLogout,
	userRegister,
	userUpdate,
} from "modules/actions";
import {errorsManagerSaga} from "modules/sagas/errors";
import {
	acceptNextSeasonTermsSaga,
	acceptTermsSaga,
	getCurrentUserSaga,
	loginSaga,
	logoutSaga,
	registerSaga,
	requestResetPasswordSaga,
	resetPasswordSaga,
	updateUserDetailsSaga,
	updateUserSaga,
	acceptViewWelcomeModalSaga,
} from "modules/sagas/user";
import {
	fetchSwingsSaga,
	pollingMessageJSONSaga,
	requestAllPlayersJSONSaga,
	requestCountriesJSONSaga,
	requestEventsJSONSaga,
	requestFantasyLandingSettings,
	requestFantasyTiles,
	requestFaqJSONSaga,
	requestGameRulesJSONSaga,
	requestPlayersJSONSaga,
	requestPlayerStatsJSONSaga,
	requestPrivacyJSONSaga,
	requestPrizesJSONSaga,
	requestScheduleJSONSaga,
	requestSeasonStatsJSONSaga,
	requestTermsJSONSaga,
} from "modules/sagas/json";
import {postContactSaga} from "modules/sagas/contact";
import {
	autofillTeamSaga,
	clearWithdrawnSaga,
	joinTileSaga,
	removeBoosterSaga,
	saveBoosterSaga,
	saveTeamFromLocalStorageSaga,
	saveTeamSaga,
	showMyTeamFullSaga,
	showMyTeamSaga,
	showRivalTeamSaga,
} from "modules/sagas/team";
import {fetchLiveScoresSaga} from "modules/sagas/liveScoring";
import {
	createLeagueSaga,
	deleteRollbackLeagueSaga,
	fetchRankingsMonthsSaga,
	globalSwingsRankingsSaga,
	joinToLeagueSaga,
	leagueMonthlyRankingsSaga,
	leagueRankingsSaga,
	leagueUsersListSaga,
	leaveLeagueSaga,
	loadMoreGlobalSwingsRankingsSaga,
	loadMoreLeagueMonthlyRankingsSaga,
	loadMoreLeagueRankingsSaga,
	loadMoreLeaguesForJoinSaga,
	loadMoreLeagueUsersListSaga,
	loadMoreRolexRankingsSaga,
	loadMoreTopTenRankingsSaga,
	rollbackAllLeaguesSaga,
	rollbackLeagueSaga,
	removeUserFromLeagueSaga,
	rolexRankingsSaga,
	searchLeaguesForJoinSaga,
	sendLeagueInvitesSaga,
	showLeagueSaga,
	showLeaguesForJoinSaga,
	showMyLeaguesSaga,
	topTenRankingsSaga,
	updateLeagueSaga,
	tilesLeaderboardSaga,
	loadMoreTilesLeaderboardSaga,
} from "modules/sagas/leagues";

/**
 * Root saga that connect sagas with actions.
 */
export const rootSaga = function* (): SagaIterator {
	yield all([
		call(pollingMessageJSONSaga),
		/**
		 * JSON
		 */
		takeLatest(requestAllPlayersJSON, requestAllPlayersJSONSaga),
		takeLatest(requestPlayersJSON, requestPlayersJSONSaga),
		takeLatest(requestPlayerStatsJSON, requestPlayerStatsJSONSaga),
		takeLatest(requestEventsJSON, requestEventsJSONSaga),
		takeLatest(requestCountriesJSON, requestCountriesJSONSaga),
		takeLatest(requestSeasonStatsJSON, requestSeasonStatsJSONSaga),
		takeLatest(fetchLandingTiles, requestFantasyTiles),
		takeLatest(fetchLandingSettings, requestFantasyLandingSettings),

		takeLatest(fetchFaq, requestFaqJSONSaga),
		takeLatest(fetchGameRules, requestGameRulesJSONSaga),
		takeLatest(fetchPrizes, requestPrizesJSONSaga),
		takeLatest(fetchTerms, requestTermsJSONSaga),
		takeLatest(fetchPrivacy, requestPrivacyJSONSaga),
		takeLatest(fetchSchedule, requestScheduleJSONSaga),
		takeLatest(fetchSwingsJsonRequest, fetchSwingsSaga),

		/**
		 * Common
		 */
		takeLatest(globalError, errorsManagerSaga),
		takeLatest(postContact, postContactSaga),
		takeLatest(subscribeToLiveScores, fetchLiveScoresSaga),

		/**
		 * Auth
		 */
		takeLatest(userLogin, loginSaga),
		takeLatest(userRegister, registerSaga),
		takeLatest(userLogout, logoutSaga),
		takeLatest(requestResetPassword, requestResetPasswordSaga),
		takeLatest(resetPassword, resetPasswordSaga),

		/**
		 * User
		 */
		takeLatest(requestCurrentUser, getCurrentUserSaga),
		takeLatest(userUpdate, updateUserSaga),
		takeLatest(userInfoUpdate, updateUserDetailsSaga),
		takeLatest(userAcceptTerms, acceptTermsSaga),
		takeLatest(userAcceptNextSeasonTerms, acceptNextSeasonTermsSaga),
		takeLatest(userAcceptViewWelcomeModal, acceptViewWelcomeModalSaga),

		/**
		 * Team and team depending
		 */
		takeLatest(requestMyTeam, showMyTeamSaga),
		takeLatest(fetchFullTeam, showMyTeamFullSaga),
		takeLatest(requestRivalTeam, showRivalTeamSaga),
		takeLatest(saveTeam, saveTeamSaga),
		takeLatest(saveTeamFromLocalStorage, saveTeamFromLocalStorageSaga),
		takeLatest(autoFillTeam, autofillTeamSaga),
		takeLatest(clearWithdrawn, clearWithdrawnSaga),
		takeLatest(saveBooster, saveBoosterSaga),
		takeLatest(removeBooster, removeBoosterSaga),
		takeLatest(joinTileRequest, joinTileSaga),

		/**
		 * Leagues
		 */
		takeLatest(createLeague, createLeagueSaga),
		takeLatest(inviteToLeague, sendLeagueInvitesSaga),
		takeLatest(showMyLeagues, showMyLeaguesSaga),
		takeLatest(showLeaguesForJoin, showLeaguesForJoinSaga),
		takeLatest(searchLeaguesForJoin, searchLeaguesForJoinSaga),
		takeLatest(loadMoreLeaguesForJoin, loadMoreLeaguesForJoinSaga),
		takeLatest(joinToLeague, joinToLeagueSaga),
		takeLatest(showLeague, showLeagueSaga),
		takeLatest(leaveLeague, leaveLeagueSaga),
		takeLatest(updateLeague, updateLeagueSaga),

		/**
		 * Rankings
		 */
		takeLatest(fetchLeagueRankings, leagueRankingsSaga),
		takeLatest(fetchMoreLeagueRankings, loadMoreLeagueRankingsSaga),
		takeLatest(fetchLeagueMonthlyRankings, leagueMonthlyRankingsSaga),
		takeLatest(fetchMoreLeagueMonthlyRankings, loadMoreLeagueMonthlyRankingsSaga),
		takeLatest(fetchRolexRankings, rolexRankingsSaga),
		takeLatest(fetchMoreRolexRankings, loadMoreRolexRankingsSaga),
		takeLatest(fetchTopTenRankings, topTenRankingsSaga),
		takeLatest(fetchMoreTopTenRankings, loadMoreTopTenRankingsSaga),
		takeLatest(fetchGlobalSwingsRankings, globalSwingsRankingsSaga),
		takeLatest(fetchTilesLeaderboard, tilesLeaderboardSaga),
		takeLatest(fetchMoreTilesLeaderboard, loadMoreTilesLeaderboardSaga),
		takeLatest(fetchMoreGlobalSwingsRankings, loadMoreGlobalSwingsRankingsSaga),
		takeLatest(rollbackAllLeaguesRequest, rollbackAllLeaguesSaga),
		takeLatest(rollbackLeagueRequest, rollbackLeagueSaga),
		takeLatest(deleteRollbackLeagueRequest, deleteRollbackLeagueSaga),
		takeLatest(fetchRankingsMonthsRequest, fetchRankingsMonthsSaga),

		takeLatest(fetchLeagueUsersList, leagueUsersListSaga),
		takeLatest(fetchMoreLeagueUsersList, loadMoreLeagueUsersListSaga),
		takeLatest(removeUserFromLeague, removeUserFromLeagueSaga),
	]);
};

export * from "./errors";
